import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Check, ArrowSquareOut, UsersThree, ArrowCounterClockwise, Play, Pause } from "@phosphor-icons/react";
import { toast } from "sonner";
import { FB_GROUPS } from "../lib/fbGroups";
import { openSharePopup } from "../lib/sharePopup";
import { api } from "../lib/api";

const MEMORY_KEY = "fbBlastMemory";
const SELECT_KEY = "fbBlastSelection";

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function loadMemory() {
  try {
    const raw = JSON.parse(localStorage.getItem(MEMORY_KEY));
    if (raw && raw.date === todayStr()) {
      return { posted: raw.posted || [], skipped: raw.skipped || [] };
    }
  } catch { /* corrupt or missing */ }
  return { posted: [], skipped: [] };
}

function saveMemory(posted, skipped) {
  try {
    localStorage.setItem(MEMORY_KEY, JSON.stringify({ date: todayStr(), posted, skipped }));
  } catch { /* storage unavailable */ }
}

function loadSelection() {
  try {
    const raw = JSON.parse(localStorage.getItem(SELECT_KEY));
    if (Array.isArray(raw)) {
      const valid = raw.filter((u) => FB_GROUPS.some((g) => g.url === u));
      if (valid.length) return valid;
    }
  } catch { /* corrupt or missing */ }
  return FB_GROUPS.map((g) => g.url);
}

function saveSelection(sel) {
  try { localStorage.setItem(SELECT_KEY, JSON.stringify(sel)); } catch { /* noop */ }
}

export default function GroupBlastModal({ open, onClose, caption }) {
  const [posted, setPosted] = useState([]);
  const [skipped, setSkipped] = useState([]);
  const [selected, setSelected] = useState(loadSelection);
  const [auto, setAuto] = useState(false);
  const [secs, setSecs] = useState(0);
  const [delay, setDelay] = useState(() => Number(localStorage.getItem("fbBlastDelay")) || 30);
  const winRef = useRef(null);
  const autoQueue = useRef([]);
  const autoIdx = useRef(0);
  const secsRef = useRef(0);

  useEffect(() => {
    if (open) {
      const mem = loadMemory();
      setPosted(mem.posted);
      setSkipped(mem.skipped);
      setSelected(loadSelection());
      if (navigator.clipboard && caption) {
        navigator.clipboard.writeText(caption).catch(() => {});
        toast.success("Caption + link copied — paste into each group post", { duration: 4000 });
      }
      if (mem.posted.length > 0) {
        toast.info(`Resuming today's blast — ${mem.posted.length} group${mem.posted.length === 1 ? "" : "s"} already posted`, { duration: 4000 });
      }
    } else {
      setAuto(false);
    }
  }, [open, caption]);

  useEffect(() => {
    if (!auto) return;
    const t = setInterval(() => {
      if (!winRef.current || winRef.current.closed) {
        setAuto(false);
        toast.info("Popup was closed — auto-advance paused. Hit Auto-blast to resume.");
        return;
      }
      secsRef.current -= 1;
      if (secsRef.current <= 0) {
        const nextIdx = autoIdx.current + 1;
        if (nextIdx >= autoQueue.current.length) {
          setAuto(false);
          toast.success("Auto-blast complete — all selected groups loaded!");
          return;
        }
        autoIdx.current = nextIdx;
        const g = autoQueue.current[nextIdx];
        try {
          winRef.current.location.href = g.url;
        } catch {
          winRef.current = openSharePopup(g.url);
        }
        logBlast("posted", g);
        setPosted((prev) => {
          const p = [...prev, g.url];
          saveMemory(p, skipped);
          return p;
        });
        secsRef.current = delay;
      }
      setSecs(secsRef.current);
    }, 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auto, delay]);

  if (!open) return null;

  const handled = [...posted, ...skipped];
  const queue = FB_GROUPS.filter((g) => selected.includes(g.url));
  const current = queue.find((g) => !handled.includes(g.url));
  const finished = !current;
  const handledInQueue = queue.filter((g) => handled.includes(g.url)).length;
  const allSelected = selected.length === FB_GROUPS.length;

  const toggleGroup = (url) => {
    setSelected((prev) => {
      const next = prev.includes(url) ? prev.filter((u) => u !== url) : [...prev, url];
      saveSelection(next);
      return next;
    });
  };

  const toggleAll = () => {
    const next = allSelected ? [] : FB_GROUPS.map((g) => g.url);
    saveSelection(next);
    setSelected(next);
  };

  const logBlast = (action, group) => {
    api.post("/metrics/blast-log", {
      action,
      group_name: group.name,
      group_url: group.url,
      caption_head: (caption || "").split("\n")[0].slice(0, 160),
    }).catch(() => {});
  };

  const openCurrent = () => {
    if (navigator.clipboard && caption) navigator.clipboard.writeText(caption).catch(() => {});
    openSharePopup(current.url);
    logBlast("posted", current);
    const p = [...posted, current.url];
    setPosted(p);
    saveMemory(p, skipped);
  };

  const skip = () => {
    logBlast("skipped", current);
    const s = [...skipped, current.url];
    setSkipped(s);
    saveMemory(posted, s);
  };

  const startAuto = () => {
    const remaining = queue.filter((g) => !handled.includes(g.url));
    if (!remaining.length) return;
    if (navigator.clipboard && caption) navigator.clipboard.writeText(caption).catch(() => {});
    const win = openSharePopup(remaining[0].url);
    if (!win) {
      toast.error("Popup blocked — allow popups for this site and try again.");
      return;
    }
    winRef.current = win;
    autoQueue.current = remaining;
    autoIdx.current = 0;
    logBlast("posted", remaining[0]);
    const p = [...posted, remaining[0].url];
    setPosted(p);
    saveMemory(p, skipped);
    secsRef.current = delay;
    setSecs(delay);
    setAuto(true);
  };

  const changeDelay = (v) => {
    setDelay(v);
    localStorage.setItem("fbBlastDelay", String(v));
  };

  const resetToday = () => {
    setPosted([]);
    setSkipped([]);
    saveMemory([], []);
    toast.success("Checklist reset — starting from the top");
  };

  const hasMemory = posted.length > 0 || skipped.length > 0;

  return createPortal(
    <div
      data-testid="group-blast-modal"
      className="fixed inset-0 z-[1400] bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm bg-tac-bg border border-tac-border rounded-sm shadow-2xl flex flex-col max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-tac-border">
          <UsersThree size={16} weight="fill" style={{ color: "#1877F2" }} />
          <div className="text-sm font-display font-bold text-tac-text">Post to Groups</div>
          {hasMemory && (
            <button
              data-testid="group-blast-reset"
              onClick={resetToday}
              title="Clear today's checklist and start over"
              className="ml-auto flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-tac-dim hover:text-tac-text border border-tac-border rounded-sm transition-colors"
            >
              <ArrowCounterClockwise size={11} weight="bold" />
              Start over
            </button>
          )}
          <button data-testid="group-blast-close" onClick={onClose} className={`${hasMemory ? "" : "ml-auto "}text-tac-dim hover:text-tac-text p-1`}>
            <X size={16} weight="bold" />
          </button>
        </div>

        <div className="px-4 py-3 border-b border-tac-border">
          {queue.length === 0 ? (
            <div className="text-[11px] text-tac-faint text-center py-2">
              No groups selected — check the groups below to build your blast list.
            </div>
          ) : auto ? (
            <>
              <div className="text-[10px] font-bold uppercase tracking-wider text-tac-safe mb-1 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-tac-safe animate-pulse" />
                Auto-blasting · group {autoIdx.current + 1} of {autoQueue.current.length} · next in {secs}s
              </div>
              <div className="text-[12px] font-bold text-tac-text mb-1.5 truncate">
                Now: {autoQueue.current[autoIdx.current]?.name}
              </div>
              <button
                data-testid="group-blast-pause"
                onClick={() => setAuto(false)}
                className="w-full flex items-center justify-center gap-2 px-3 py-2.5 border border-tac-border text-tac-text font-bold text-[13px] rounded-sm hover:bg-tac-surface2 transition-colors"
              >
                <Pause size={14} weight="fill" /> Pause auto-blast
              </button>
              <div className="mt-2 text-[10px] text-tac-faint leading-snug">
                Keep the popup window open — paste &amp; post in it, and it loads the next group
                automatically every {delay}s. Closing the popup pauses the run.
              </div>
            </>
          ) : !finished ? (
            <>
              <div className="text-[10px] font-bold uppercase tracking-wider text-tac-faint mb-1">
                Group {handledInQueue + 1} of {queue.length} · caption is copied — just paste &amp; post
              </div>
              <button
                data-testid="group-blast-open-next"
                onClick={openCurrent}
                className="w-full flex items-center justify-between gap-2 px-3 py-2.5 bg-tac-primary text-white font-bold text-[13px] rounded-sm hover:bg-tac-primary/90 transition-colors"
              >
                <span className="truncate">Open &amp; post: {current.name}</span>
                <ArrowSquareOut size={14} weight="bold" className="shrink-0" />
              </button>
              <button
                data-testid="group-blast-skip"
                onClick={skip}
                className="mt-1.5 w-full px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-tac-dim hover:text-tac-text border border-tac-border rounded-sm transition-colors"
              >
                Skip this group
              </button>
              <div className="mt-2 flex items-center gap-1.5">
                <button
                  data-testid="group-blast-auto-start"
                  onClick={startAuto}
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 border border-tac-safe/50 text-tac-safe font-bold text-[11px] uppercase tracking-wider rounded-sm hover:bg-tac-safe/10 transition-colors"
                >
                  <Play size={12} weight="fill" /> Auto-blast
                </button>
                <select
                  data-testid="group-blast-auto-delay"
                  value={delay}
                  onChange={(e) => changeDelay(Number(e.target.value))}
                  className="bg-tac-surface border border-tac-border text-tac-text text-[11px] font-bold rounded-sm px-1.5 py-2 outline-none"
                >
                  <option value={20}>20s</option>
                  <option value={30}>30s</option>
                  <option value={45}>45s</option>
                  <option value={60}>60s</option>
                </select>
              </div>
              <div className="mt-2 text-[10px] text-tac-faint leading-snug" data-testid="group-blast-explainer">
                ⚠️ Facebook doesn't allow auto-posting to groups — a human must hit Post in each
                one. <span className="text-tac-dim font-bold">Auto-blast</span> keeps one popup
                open and loads the next group on a timer, so it's just paste → Post → wait.
              </div>
            </>
          ) : (
            <div className="text-center py-2">
              <div className="text-lg mb-1">🎉</div>
              <div className="text-sm font-bold text-tac-text">All {queue.length} selected groups done!</div>
              <div className="text-[11px] text-tac-dim mt-0.5">Posted to {posted.length}, skipped {skipped.length}.</div>
            </div>
          )}
        </div>

        <div className="overflow-y-auto tac-scroll py-1">
          <label
            data-testid="group-blast-select-all"
            className="flex items-center gap-2 px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-tac-text cursor-pointer border-b border-tac-border/60 sticky top-0 bg-tac-bg"
          >
            <input
              type="checkbox"
              checked={allSelected}
              onChange={toggleAll}
              className="accent-[#1877F2]"
            />
            All groups ({FB_GROUPS.length})
          </label>
          {FB_GROUPS.map((g, i) => {
            const isPosted = posted.includes(g.url);
            const isSkipped = skipped.includes(g.url);
            const isSelected = selected.includes(g.url);
            const isCurrent = current && current.url === g.url;
            return (
              <label
                key={g.url}
                data-testid={`group-blast-row-${i}`}
                className={`flex items-center gap-2 px-4 py-1.5 text-[11px] font-semibold cursor-pointer ${
                  isCurrent ? "bg-tac-surface2 text-tac-text" : isPosted ? "text-tac-safe" : isSkipped ? "text-tac-faint line-through" : isSelected ? "text-tac-dim" : "text-tac-faint"
                }`}
              >
                <input
                  type="checkbox"
                  data-testid={`group-blast-check-${i}`}
                  checked={isSelected}
                  onChange={() => toggleGroup(g.url)}
                  className="accent-[#1877F2] shrink-0"
                />
                {isPosted ? (
                  <Check size={12} weight="bold" className="text-tac-safe shrink-0" />
                ) : (
                  <span className="w-3 text-center shrink-0">{isCurrent ? "→" : "·"}</span>
                )}
                <span className="truncate">{g.name}</span>
              </label>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
}
